/**
 * The session register's column registry.
 *
 * Column ids are the ones the dashboard has always persisted (`CORE_COLUMN_IDS`,
 * `question_<id>`, `cf_<slug>`, `pf_<slug>`) plus `attendeeName`. Each id gets
 * a scope (does the value belong to the attendee or to the booking?) and a
 * kind (how should the value render?). Scope decides which cells merge across
 * sibling rows; kind decides the cell renderer and default width. Shared by
 * the table, the list, CSV, print and, later, the assistant tools.
 */
export type ColumnScope = "attendee" | "booking";
export type ColumnKind =
  | "identity" | "checkbox" | "select" | "multiSelect" | "text" | "longText" | "number"
  | "date" | "rating" | "currency" | "url" | "email" | "phone" | "file"
  | "status" | "tickets" | "actions";

export type ColumnDescriptor = {
  id: string;
  scope: ColumnScope;
  kind: ColumnKind;
  label: string;
  fullLabel: string;
  canHide: boolean;
};

type QuestionLike = { _id: string; title: string; typeOfInput?: string | null; isPerGuest?: boolean | null; shortLabel?: string | null };
type FieldLike = { slug: string; label: string; fieldType: string; shortLabel?: string | null };

export type DescribeColumnsInput = {
  questions: QuestionLike[];
  customerFields: FieldLike[];
  participantFields: FieldLike[];
  participantLabel: string;
  showSalonName: boolean;
  includeMoney: boolean;
  includeActions: boolean;
  /**
   * Add the attendee's own ticket (`attendeeTicket`): on when any booking read
   * has seats, since only a seat knows which ticket a person holds.
   */
  includeSeatTicket?: boolean;
};

/** Charge columns, hidden from staff who may not see earnings. */
export const MONEY_COLUMN_IDS: readonly string[] = ["userCharged", "discount", "grossTotal"];
const MONEY_IDS = new Set(MONEY_COLUMN_IDS);

/**
 * Columns the table pins open (`enableHiding: false`): without a name there is
 * no row to read, without the badges no booking state, and the row menu is the
 * only way to act on a booking.
 */
const LOCKED_COLUMN_IDS = new Set(["attendeeName", "buyerName", "status", "actions"]);
const canHideColumn = (id: string) => !LOCKED_COLUMN_IDS.has(id);

/** Booking-scoped core columns in their default order. */
export const CORE_COLUMN_IDS_ORDERED: readonly string[] = [
  "bookingID", "buyerName", "status", "bookingNotes", "buyerEmail", "buyerPhone", "numberOfGuests",
  "attendees", "tickets", "addOns", "userCharged", "salonName", "discount", "grossTotal", "source"
];

const CORE: Record<string, { kind: ColumnKind; label: string }> = {
  attended: { kind: "checkbox", label: "Check In" },
  bookingID: { kind: "text", label: "Booking ID" },
  buyerName: { kind: "text", label: "Customer" },
  status: { kind: "status", label: "Badges" },
  // The staff note on the booking (`booking.notes`), once an icon inside the
  // Customer cell: its own column so it can be read, shown or hidden.
  bookingNotes: { kind: "longText", label: "Booking notes" },
  buyerEmail: { kind: "email", label: "Email" },
  buyerPhone: { kind: "phone", label: "Phone" },
  numberOfGuests: { kind: "number", label: "Guests" },
  attendees: { kind: "text", label: "Attendees" },
  tickets: { kind: "tickets", label: "Tickets" },
  addOns: { kind: "text", label: "Add-ons" },
  // How the booking was paid and, where money moved for it, how much: the
  // register reads `booking.paymentSummary`, which the API classifies with the
  // income ledger's rule. The id is the column's saved key and does not change.
  userCharged: { kind: "currency", label: "Payment" },
  salonName: { kind: "text", label: "Salon Name" },
  discount: { kind: "currency", label: "Discount" },
  grossTotal: { kind: "currency", label: "Gross Total" },
  source: { kind: "text", label: "Source" },
  actions: { kind: "actions", label: "" }
};

/** Default English label per core column id — the labels `describeColumns` uses. */
export const CORE_COLUMN_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(CORE).map(([id, column]) => [id, column.label])
);

export function questionKind(typeOfInput: string | null | undefined): ColumnKind {
  switch (typeOfInput) {
    case "booleanInput": return "checkbox";
    case "singleSelect": return "select";
    case "multiSelect": return "multiSelect";
    case "longTextInput": return "longText";
    case "numberInput": return "number";
    case "dateInput":
    case "dateTimeInput": return "date";
    case "ratingInput": return "rating";
    case "urlInput": return "url";
    case "currencyInput": return "currency";
    case "fileInput": return "file";
    default: return "text";
  }
}

export function fieldKind(fieldType: string): ColumnKind {
  switch (fieldType) {
    case "checkbox": return "checkbox";
    case "select": return "select";
    case "multiSelect": return "multiSelect";
    case "longText": return "longText";
    case "number": return "number";
    case "date":
    case "dateOfBirth":
    case "dateTime": return "date";
    case "rating": return "rating";
    case "currency": return "currency";
    case "url": return "url";
    case "email": return "email";
    case "phone": return "phone";
    default: return "text";
  }
}

/**
 * The one short-label rule: a trimmed, non-empty short label wins, otherwise
 * the full label. Public so every surface (registry, dashboard column labels,
 * exports) resolves it the same way instead of re-implementing the fallback.
 */
export function resolveColumnLabel(shortLabel: string | null | undefined, fullLabel: string): string {
  const short = (shortLabel ?? "").trim();
  return short.length > 0 ? short : fullLabel;
}

export function describeColumns(input: DescribeColumnsInput): ColumnDescriptor[] {
  const out: ColumnDescriptor[] = [];
  const core = (id: string, scope: ColumnScope, label?: string) =>
    out.push({
      id,
      scope,
      kind: CORE[id].kind,
      label: label ?? CORE[id].label,
      fullLabel: label ?? CORE[id].label,
      canHide: canHideColumn(id)
    });

  const questionColumn = (q: QuestionLike): ColumnDescriptor => ({
    id: `question_${q._id}`,
    scope: q.isPerGuest ? "attendee" : "booking",
    kind: questionKind(q.typeOfInput),
    label: resolveColumnLabel(q.shortLabel, q.title),
    fullLabel: q.title,
    canHide: true
  });
  const fieldColumn = (prefix: "cf_" | "pf_", f: FieldLike): ColumnDescriptor => ({
    id: `${prefix}${prefix === "pf_" ? f.slug.replace(/^p_/, "") : f.slug}`,
    scope: prefix === "pf_" ? "attendee" : "booking",
    kind: fieldKind(f.fieldType),
    label: resolveColumnLabel(f.shortLabel, f.label),
    fullLabel: f.label,
    canHide: true
  });

  const questions = input.questions.filter(q => q.typeOfInput !== "noteInput");

  core("attended", "booking");
  out.push({
    id: "attendeeName",
    scope: "attendee",
    kind: "identity",
    label: input.participantLabel,
    fullLabel: input.participantLabel,
    canHide: canHideColumn("attendeeName")
  });
  if (input.includeSeatTicket) {
    out.push({ id: "attendeeTicket", scope: "attendee", kind: "text", label: "Ticket", fullLabel: "Ticket", canHide: true });
  }
  questions.filter(q => q.isPerGuest).forEach(q => out.push(questionColumn(q)));
  input.participantFields.forEach(f => out.push(fieldColumn("pf_", f)));

  for (const id of CORE_COLUMN_IDS_ORDERED) {
    if (id === "salonName" && !input.showSalonName) continue;
    if (MONEY_IDS.has(id) && !input.includeMoney) continue;
    core(id, "booking", id === "attendees" ? input.participantLabel : undefined);
  }
  questions.filter(q => !q.isPerGuest).forEach(q => out.push(questionColumn(q)));
  input.customerFields.forEach(f => out.push(fieldColumn("cf_", f)));
  if (input.includeActions) core("actions", "booking");
  return out;
}

export function groupIdsByScope(ids: string[], descriptors: ColumnDescriptor[]): { attendee: string[]; booking: string[] } {
  const scopeOf = new Map(descriptors.map(d => [d.id, d.scope]));
  const attendee: string[] = [];
  const booking: string[] = [];
  for (const id of ids) (scopeOf.get(id) === "attendee" ? attendee : booking).push(id);
  return { attendee, booking };
}
